import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GC, s_TI } from '../../shared/styles';
import { EXERCISES } from '../exercises/constants';

const BASE = process.env.REACT_APP_API_URL || '/api';

function GroupSessionScreen({ groupId, exerciseId, scheduleId, currentUserId, userName, lang, groupName, onBack }) {
  const nl = lang === 'nl';
  const exercise = EXERCISES.find(e => e.id === exerciseId) || { id: exerciseId, icon: '🧩', label: exerciseId, labelEN: exerciseId, desc: '', descEN: '' };
  const [elapsed, setElapsed] = useState(0);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [done, setDone] = useState(false);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const lastMsgId = useRef(0);
  const chatEndRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setElapsed(p => p + 1), 1000);
    fetch(`${BASE}/groups/${groupId}/messages`)
      .then(r => r.ok ? r.json() : [])
      .then(msgs => {
        setMessages(msgs);
        if (msgs.length) lastMsgId.current = Math.max(...msgs.map(m => m.id));
      }).catch(() => {});
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${BASE}/groups/${groupId}/messages?since=${lastMsgId.current}`);
        if (r.ok) {
          const newMsgs = await r.json();
          if (newMsgs.length) {
            setMessages(prev => [...prev, ...newMsgs]);
            lastMsgId.current = Math.max(...newMsgs.map(m => m.id));
          }
        }
      } catch {}
    }, 3000);
    return () => { clearInterval(timerRef.current); clearInterval(pollRef.current); };
  }, [groupId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    const text = msgText.trim();
    setMsgText('');
    const tempMsg = { id: Date.now(), userId: currentUserId, userName, text, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) };
    setMessages(prev => [...prev, tempMsg]);
    try {
      const r = await fetch(`${BASE}/groups/${groupId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUserId, userName, text }),
      });
      if (r.ok) {
        const d = await r.json();
        if (d.id && d.id > lastMsgId.current) lastMsgId.current = d.id;
      }
    } catch {}
  };

  const finishSession = async () => {
    clearInterval(timerRef.current);
    clearInterval(pollRef.current);
    try {
      await fetch(`${BASE}/groups/${groupId}/schedule/${scheduleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'done' }),
      });
    } catch {}
    setDone(true);
  };

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
        <div style={{ color: C.white, fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{nl ? 'Sessie voltooid!' : 'Session complete!'}</div>
        <div style={{ color: C.silver, fontSize: 14, marginBottom: 24 }}>{fmt(elapsed)} {nl ? 'samen geoefend' : 'exercised together'}</div>
        <button style={s_PB({ padding: '14px 32px' })} onClick={onBack}>{nl ? 'Terug naar groep' : 'Back to group'}</button>
      </div>
    );
  }

  return (
    <div className="slideIn">
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12, textAlign: 'left' }}>
        ← {nl ? 'Terug' : 'Back'}
      </button>

      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 52, marginBottom: 8 }}>{exercise.icon}</div>
        <div style={{ color: C.white, fontSize: 18, fontWeight: 700 }}>{nl ? exercise.label : (exercise.labelEN || exercise.label)}</div>
        <div style={{ color: C.silver, fontSize: 12 }}>{groupName}</div>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: 42, color: _T.accent, marginTop: 10 }}>{fmt(elapsed)}</div>
      </div>

      {(exercise.desc || exercise.descEN) && (
        <div style={{ ...s_GC({ padding: 14, marginBottom: 12 }), textAlign: 'center' }}>
          <div style={{ color: C.silver2, fontSize: 13, lineHeight: 1.7 }}>{nl ? exercise.desc : (exercise.descEN || exercise.desc)}</div>
        </div>
      )}

      <div style={{ ...s_GC({ padding: 12 }), maxHeight: 240, overflowY: 'auto', marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>💬 {nl ? 'GROEPSCHAT' : 'GROUP CHAT'}</div>
        {messages.length === 0 && (
          <div style={{ color: C.silver, fontSize: 12, textAlign: 'center', padding: 8 }}>{nl ? 'Nog geen berichten' : 'No messages yet'}</div>
        )}
        {messages.map((m, i) => {
          const isMe = m.userId === currentUserId;
          return (
            <div key={m.id || i} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
              <div style={{ maxWidth: '80%', background: isMe ? `${_T.accent}22` : _T.bg4, border: `1px solid ${isMe ? _T.accent + '44' : _T.cardBorderBot}`, borderRadius: 10, padding: '6px 10px' }}>
                {!isMe && <div style={{ fontSize: 9, color: _T.accent, fontWeight: 700, marginBottom: 2 }}>{m.userName}</div>}
                <div style={{ color: C.white, fontSize: 12 }}>{m.text}</div>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input value={msgText} onChange={e => setMsgText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendMessage()}
          placeholder={nl ? 'Bericht...' : 'Message...'}
          style={s_TI({ flex: 1, padding: '10px 12px', fontSize: 13 })} />
        <button style={s_PB({ padding: '10px 14px' })} onClick={sendMessage}>→</button>
      </div>

      <button
        style={{ background: 'linear-gradient(135deg,#2A9E82,#1a7a62)', border: 'none', borderRadius: 14, padding: 14, fontWeight: 700, fontSize: 15, cursor: 'pointer', color: C.white, width: '100%' }}
        onClick={finishSession}>
        ✅ {nl ? 'Sessie voltooien' : 'Finish session'}
      </button>
    </div>
  );
}

export default GroupSessionScreen;
